import { useState } from "react";
import {
  MessageSquare, Plus, Edit2, Trash2, Send, X,
  Users, BarChart2, CheckCircle, Layers, ChevronDown,
  Smartphone, AlertCircle,
} from "lucide-react";
import { useAdmin } from "../admin-context";
import DataTable from "../DataTable";

// ── Constants ─────────────────────────────────────────────────────────────────

const SMS_LIMIT   = 160; // chars per segment
const MAX_CHARS   = 480; // 3 segments max

const SEED = [
  {
    id: 1,
    name: "Flash Sale Alert",
    message: "Gamapa Foods: Flash sale today only! 30% off all orders. Use code FLASH30 at checkout. Order now: gamapa.com",
    recipient_group: "all_customers",
    status: "sent",
    sent_count: 980,
    delivered_count: 962,
    created_at: "2025-03-22T10:00:00Z",
  },
  {
    id: 2,
    name: "Order Ready Reminder",
    message: "Hi {{name}}, your Gamapa Foods order #{{order_id}} is ready for pickup! Come grab it while it's hot 🔥",
    recipient_group: "recent_orders",
    status: "sent",
    sent_count: 340,
    delivered_count: 335,
    created_at: "2025-04-05T14:30:00Z",
  },
  {
    id: 3,
    name: "Loyalty Points Nudge",
    message: "Hey {{name}}! You have unused loyalty points at Gamapa Foods. Redeem them on your next order. gamapa.com/loyalty",
    recipient_group: "loyalty_members",
    status: "draft",
    sent_count: 0,
    delivered_count: 0,
    created_at: "2025-04-09T11:00:00Z",
  },
];

const RECIPIENT_GROUPS = [
  { value: "all_customers",   label: "All Customers"     },
  { value: "new_customers",   label: "New Customers"     },
  { value: "loyalty_members", label: "Loyalty Members"   },
  { value: "recent_orders",   label: "Recent Orders"     },
  { value: "inactive",        label: "Inactive (30d+)"   },
];

const TEMPLATES = [
  { id: "promo",   label: "Promo",    text: "Hi {{name}}! Gamapa Foods has a special deal just for you. Use code SAVE15 for 15% off your next order. gamapa.com" },
  { id: "flash",   label: "Flash",    text: "⚡ Flash sale! Gamapa Foods: [offer] today only. Order now: gamapa.com" },
  { id: "loyalty", label: "Loyalty",  text: "Hi {{name}}, you have loyalty points waiting at Gamapa Foods! Redeem them on your next order. gamapa.com/loyalty" },
  { id: "reminder",label: "Reminder", text: "Hi {{name}}, don't forget about your cart at Gamapa Foods! Complete your order: gamapa.com/cart" },
];

const STATUS_CHIP = {
  draft:     "bg-gray-100 text-gray-600",
  scheduled: "bg-blue-100 text-blue-700",
  sent:      "bg-green-100 text-green-700",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const segments = (text) => Math.max(1, Math.ceil(text.length / SMS_LIMIT));

const deliveryRate = (sent, delivered) =>
  sent > 0 ? `${((delivered / sent) * 100).toFixed(1)}%` : "—";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const groupLabel = (val) =>
  RECIPIENT_GROUPS.find(g => g.value === val)?.label || val;

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 ${color}`}>
      <Icon size={20} />
    </div>
    <div>
      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

// Phone preview of the SMS
const PhonePreview = ({ message }) => (
  <div className="flex justify-center">
    <div className="relative w-48">
      {/* Phone shell */}
      <div className="border-4 border-gray-800 rounded-[2rem] overflow-hidden bg-gray-100 shadow-xl">
        <div className="bg-gray-800 h-5 flex items-center justify-center">
          <div className="w-10 h-1 rounded-full bg-gray-600" />
        </div>
        <div className="bg-white min-h-[200px] p-3 space-y-2">
          <p className="text-[10px] text-gray-400 text-center">Gamapa Foods</p>
          <div className="bg-gray-100 rounded-2xl rounded-tl-sm p-2.5 max-w-full">
            <p className="text-[11px] text-gray-800 leading-relaxed break-words">
              {message || <span className="text-gray-300 italic">Your message appears here…</span>}
            </p>
          </div>
        </div>
        <div className="bg-gray-800 h-5 flex items-center justify-center">
          <div className="w-6 h-6 rounded-full border-2 border-gray-600" />
        </div>
      </div>
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const SMSCampaignsPage = () => {
  const { showToast, logAudit } = useAdmin();

  const [campaigns, setCampaigns] = useState(SEED);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  const [saving, setSaving]         = useState(false);

  const blankForm = () => ({
    name: "", message: "", recipient_group: "all_customers", status: "draft",
  });

  const [form, setForm] = useState(blankForm());
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const charCount  = form.message.length;
  const segCount   = segments(form.message);
  const overLimit  = charCount > MAX_CHARS;

  const openAdd = () => {
    setEditing(null);
    setForm(blankForm());
    setDrawerOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, message: c.message, recipient_group: c.recipient_group, status: c.status });
    setDrawerOpen(true);
  };

  const applyTemplate = (id) => {
    const t = TEMPLATES.find(t => t.id === id);
    if (t) set("message", t.text);
  };

  const handleSave = async (sendNow = false) => {
    if (!form.name.trim() || !form.message.trim()) {
      showToast("Name and message are required.", "error");
      return;
    }
    if (overLimit) {
      showToast("Message exceeds 3 SMS segments (480 chars).", "error");
      return;
    }
    setSaving(true);
    await new Promise(r => setTimeout(r, 400));

    if (editing) {
      setCampaigns(prev => prev.map(c =>
        c.id === editing.id
          ? { ...c, ...form, ...(sendNow ? { status: "sent", sent_count: Math.floor(Math.random() * 800 + 100), delivered_count: Math.floor(Math.random() * 780 + 90) } : {}) }
          : c
      ));
      logAudit(sendNow ? "Sent" : "Updated", "SMS Campaign", form.name);
      showToast(sendNow ? "SMS campaign sent!" : "Campaign updated.", "success");
    } else {
      const newC = {
        id: Date.now(),
        ...form,
        sent_count: sendNow ? Math.floor(Math.random() * 500 + 50) : 0,
        delivered_count: sendNow ? Math.floor(Math.random() * 490 + 45) : 0,
        created_at: new Date().toISOString(),
        ...(sendNow ? { status: "sent" } : {}),
      };
      setCampaigns(prev => [newC, ...prev]);
      logAudit(sendNow ? "Sent" : "Created", "SMS Campaign", form.name);
      showToast(sendNow ? "SMS campaign sent!" : "Campaign saved as draft.", "success");
    }

    setSaving(false);
    setDrawerOpen(false);
  };

  const handleDelete = (id) => {
    const c = campaigns.find(x => x.id === id);
    setCampaigns(prev => prev.filter(x => x.id !== id));
    logAudit("Deleted", "SMS Campaign", c?.name || id);
    showToast("Campaign deleted.", "success");
  };

  // Stats
  const totalSent      = campaigns.filter(c => c.status === "sent").length;
  const totalDraft     = campaigns.filter(c => c.status === "draft").length;
  const totalMsgsSent  = campaigns.reduce((s, c) => s + (c.sent_count || 0), 0);

  const columns = [
    {
      key: "name", label: "Campaign", sortable: true,
      render: (val, row) => (
        <div>
          <p className="font-semibold text-gray-800 text-sm">{val}</p>
          <p className="text-xs text-gray-400 truncate max-w-[220px]">{row.message}</p>
        </div>
      ),
    },
    {
      key: "recipient_group", label: "Recipients", sortable: true,
      render: (val) => (
        <span className="flex items-center gap-1.5 text-sm text-gray-600">
          <Users size={13} className="text-gray-400" /> {groupLabel(val)}
        </span>
      ),
    },
    {
      key: "status", label: "Status", sortable: true,
      render: (val) => (
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${STATUS_CHIP[val] || "bg-gray-100 text-gray-600"}`}>
          {val}
        </span>
      ),
    },
    {
      key: "sent_count", label: "Sent", sortable: true,
      render: (val) => <span className="text-sm font-medium text-gray-700">{(val || 0).toLocaleString()}</span>,
    },
    {
      key: "delivered_count", label: "Delivery Rate", sortable: false,
      render: (val, row) => (
        <span className="text-sm font-medium text-gray-700">{deliveryRate(row.sent_count, val)}</span>
      ),
    },
    {
      key: "created_at", label: "Date", sortable: true,
      render: (val) => <span className="text-sm text-gray-400">{fmtDate(val)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <MessageSquare size={20} className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SMS Campaigns</h1>
            <p className="text-xs text-gray-400">Send text message marketing to your customers</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm transition"
        >
          <Plus size={16} /> New Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Layers}         label="Total"         value={campaigns.length}              color="bg-purple-500" />
        <StatCard icon={CheckCircle}    label="Sent"          value={totalSent}                     color="bg-green-500"  />
        <StatCard icon={Smartphone}     label="SMS Delivered" value={totalMsgsSent.toLocaleString()} color="bg-orange-500" />
        <StatCard icon={BarChart2}      label="Drafts"        value={totalDraft}                    color="bg-gray-400"   />
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={campaigns}
        searchKeys={["name", "message"]}
        searchPlaceholder="Search campaigns…"
        filters={[
          {
            key: "status",
            label: "Status",
            options: [
              { label: "Draft",     value: "draft"     },
              { label: "Scheduled", value: "scheduled" },
              { label: "Sent",      value: "sent"      },
            ],
          },
        ]}
        defaultPageSize={10}
        emptyText="No SMS campaigns yet. Create your first one!"
        actions={(row) => (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => openEdit(row)}
              className="p-2 rounded-lg border border-orange-200 bg-orange-50 text-orange-600 hover:bg-orange-100 transition"
              title="Edit"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              className="p-2 rounded-lg border border-red-100 bg-red-50 text-red-500 hover:bg-red-100 transition"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      />

      {/* Create / Edit drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editing ? "Edit SMS Campaign" : "New SMS Campaign"}
      >
        <div className="space-y-5">

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Campaign Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              placeholder="e.g. Summer Flash Sale"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition"
            />
          </div>

          {/* Recipient group */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Recipients</label>
            <div className="relative">
              <select
                value={form.recipient_group}
                onChange={e => set("recipient_group", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 appearance-none bg-white transition pr-8"
              >
                {RECIPIENT_GROUPS.map(g => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Template picker */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Load Template</label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t.id)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 transition"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message + character count */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-600">
                Message *
                <span className="ml-2 text-gray-300 font-normal">use {"{{name}}"}, {"{{order_id}}"}</span>
              </label>
              <span className={`text-xs font-semibold tabular-nums ${overLimit ? "text-red-500" : charCount > SMS_LIMIT ? "text-yellow-500" : "text-gray-400"}`}>
                {charCount}/{MAX_CHARS} · {segCount} SMS
              </span>
            </div>
            <textarea
              value={form.message}
              onChange={e => set("message", e.target.value.slice(0, MAX_CHARS))}
              rows={5}
              placeholder="Write your SMS here… Keep it short and clear."
              className={`w-full border rounded-xl px-3 py-2.5 text-sm outline-none transition resize-none
                ${overLimit
                  ? "border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-400"
                  : "border-gray-200 focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                }`}
            />
            {charCount > SMS_LIMIT && (
              <p className={`flex items-center gap-1 text-xs mt-1 ${overLimit ? "text-red-500" : "text-yellow-500"}`}>
                <AlertCircle size={11} />
                {segCount} SMS segments will be sent per recipient
                {overLimit && " — reduce message length"}
              </p>
            )}
          </div>

          {/* Phone preview */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-3">Preview</label>
            <PhonePreview message={form.message} />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={saving || overLimit}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition"
            >
              {saving ? "Saving…" : "Save Draft"}
            </button>
            <button
              type="button"
              onClick={() => handleSave(true)}
              disabled={saving || overLimit || !form.message.trim()}
              className="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition shadow-sm"
            >
              <Send size={15} /> {saving ? "Sending…" : "Send Now"}
            </button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default SMSCampaignsPage;
